
import { supabase } from '@/integrations/supabase/client';
import { Sidebar } from '@/types/roadmap';

export async function updateSidebar(templateId: string, sidebar: Sidebar): Promise<void> {
  if (!sidebar) {
    return;
  }

  // First get or create the sidebar record for this roadmap
  let sidebarId: string;
  
  const { data: sidebarData, error: sidebarFetchError } = await supabase
    .from('sidebars')
    .select('id')
    .eq('roadmap_id', templateId)
    .single();

  if (sidebarFetchError || !sidebarData) {
    // No sidebar exists, create one
    console.log('updateSidebar: No sidebar found, creating new one for template:', templateId);
    const { data: newSidebarData, error: createError } = await supabase
      .from('sidebars')
      .insert({ roadmap_id: templateId })
      .select('id')
      .single();
      
    if (createError || !newSidebarData) {
      console.error('updateSidebar: Error creating sidebar:', createError);
      throw createError;
    }
    
    sidebarId = newSidebarData.id;
  } else {
    sidebarId = sidebarData.id;
  }

  // Update sidebar components
  await updateSidebarTools(sidebarId, sidebar.usefulTools);
  await updateSidebarActivities(sidebarId, sidebar.bonusActivities);
  await updateSidebarExamples(sidebarId, sidebar.examples);
}

async function updateSidebarTools(sidebarId: string, tools: any[]): Promise<void> {
  // Clear existing tools first
  await supabase
    .from('sidebar_tools')
    .delete()
    .eq('sidebar_id', sidebarId)
    .is('prompt_field_id', null);

  // Insert new tools if any
  if (tools && Array.isArray(tools) && tools.length > 0) {
    const toolsToInsert = tools.map((tool, index) => ({
      sidebar_id: sidebarId,
      prompt_field_id: null,
      step_id: null,
      label: tool.label || '',
      url: tool.url || '',
      description: tool.description || null,
      position: index + 1
    }));

    const { error: insertError } = await supabase
      .from('sidebar_tools')
      .insert(toolsToInsert);

    if (insertError) {
      console.error('updateSidebarTools: Error inserting sidebar tools:', insertError);
      throw insertError;
    }
  }
}

async function updateSidebarActivities(sidebarId: string, activities: any[]): Promise<void> {
  // Clear existing activities first
  await supabase
    .from('sidebar_activities')
    .delete()
    .eq('sidebar_id', sidebarId);

  // Insert new activities if any
  if (activities && Array.isArray(activities) && activities.length > 0) {
    const activitiesToInsert = activities.map((activity, index) => ({
      sidebar_id: sidebarId,
      title: activity.title || '',
      description: activity.description || '',
      href: activity.href || '',
      icon_placeholder: activity.iconPlaceholder || '',
      position: index + 1
    }));

    const { error: insertError } = await supabase
      .from('sidebar_activities')
      .insert(activitiesToInsert);

    if (insertError) {
      console.error('updateSidebarActivities: Error inserting sidebar activities:', insertError);
      throw insertError;
    }
  }
}

async function updateSidebarExamples(sidebarId: string, examples: any[]): Promise<void> {
  // Clear existing examples first
  await supabase
    .from('sidebar_examples')
    .delete()
    .eq('sidebar_id', sidebarId);

  // Insert new examples if any
  if (examples && Array.isArray(examples) && examples.length > 0) {
    const examplesToInsert = examples.map((example, index) => ({
      sidebar_id: sidebarId,
      text: example.text || '',
      href: example.href || '',
      description: example.description || '',
      position: index + 1
    }));

    const { error: insertError } = await supabase
      .from('sidebar_examples')
      .insert(examplesToInsert);

    if (insertError) {
      console.error('updateSidebarExamples: Error inserting sidebar examples:', insertError);
      throw insertError;
    }
  }
}
